import { Controller } from "@hotwired/stimulus";

/**
 * The summary of a one-page form, written live just above its button
 * (29/10/2026): it replaces the « Confirmation » step of the former wizards
 * that ran in the browser. Each `value` target mirrors the field named in its
 * `data-field`:
 *   - radios or boxes → the title of what is ticked (`.ds-choice__title`);
 *   - a select, a DS select (hidden input in `.ds-select`) → the label shown;
 *   - a file field → the name of the file;
 *   - a flatpickr date → the date as shown to the person;
 *   - a text field → its value, cut at `data-max` characters.
 * Given `data-count-one` / `data-count-other` (« %{count} écoles »), the
 * target shows how many values are chosen instead of naming them; given
 * `data-checked`, it shows that text when a box is ticked. A disabled
 * field (a panel hidden by choice-reveal) counts as empty; `data-empty` is
 * shown when nothing is chosen.
 *
 * <form data-controller="form-summary"
 *       data-action="input->form-summary#update change->form-summary#update">
 *   <span data-form-summary-target="value" data-field="report[title]" data-empty="—"></span>
 */
export default class extends Controller {
  static targets = ["value"];

  connect() {
    this.update();
  }

  update() {
    this.valueTargets.forEach((target) => {
      target.textContent = this.read(target) || target.dataset.empty || "—";
    });
  }

  read(target) {
    const fields = Array.from(this.element.querySelectorAll("[name]"))
      .filter((field) => field.name === target.dataset.field && !field.disabled);
    if (fields.length === 0) return "";

    const labels = this.labels(fields);
    if (target.dataset.countOther) return this.count(target, labels.length);
    if (target.dataset.checked !== undefined) return labels.length > 0 ? target.dataset.checked : "";

    const text = labels.join(", ");
    const max = parseInt(target.dataset.max || "0", 10);
    return max > 0 && text.length > max ? `${text.slice(0, max)}…` : text;
  }

  labels(fields) {
    const choices = fields.filter((field) => field.type === "radio" || field.type === "checkbox");
    if (choices.length > 0) {
      return choices.filter((field) => field.checked).map((field) => this.choiceLabel(field));
    }
    const first = fields[0];
    if (first.tagName === "SELECT") {
      return Array.from(first.selectedOptions).filter((option) => option.value !== "")
        .map((option) => option.text.trim());
    }
    if (first.type === "file") {
      return Array.from(first.files || []).map((file) => file.name);
    }
    const filled = fields.filter((field) => field.value.trim() !== "");
    // A flatpickr date: the date as the person reads it, not its ISO value.
    if (first._flatpickr?.altInput && filled.length > 0) return [first._flatpickr.altInput.value];
    const select = first.closest(".ds-select");
    if (select && filled.length > 0) {
      return [select.querySelector("[data-ds-select-target='label']")?.textContent.trim() || filled[0].value];
    }
    return filled.map((field) => field.value.trim());
  }

  choiceLabel(input) {
    const label = input.closest("label");
    const title = label?.querySelector(".ds-choice__title") || label;
    return title?.textContent.trim() || input.value;
  }

  count(target, total) {
    if (total === 0) return "";
    const template = total === 1 ? target.dataset.countOne : target.dataset.countOther;
    return (template || "%{count}").replace("%{count}", total);
  }
}
